import { create } from "zustand";

import { api, HttpError } from "../api/rest";
import type {
  CommandAccepted,
  CommandResult,
  CommandsAccepted,
  CommandTarget,
  Order,
  PlaceOrderRequest,
  PlacePaperOrderRequest,
} from "../api/types";
import { accountLabel } from "../lib/accounts";
import type { CommandSource } from "../lib/latency";
import { useAccounts } from "./useAccounts";
import { useLatency } from "./useLatency";
import {
  emptyOrders,
  expirePending,
  hydrateOpen,
  addOptimistic,
  ingestOrder as reduceOrder,
  ingestResult as reduceResult,
  isTerminal,
  type OrdersState,
  type PendingKind,
  startPending,
} from "./orders";

/**
 * Всё нечистое про ордера: сеть, таймеры, состояние React.
 *
 * Правила слияния живут в orders.ts и проверяются тестом; здесь — только
 * оркестровка.
 *
 * Пакетирования (rAF), в отличие от useBooks и useTape, тут НЕТ намеренно:
 * событий на один ордер единицы, а лишний переход через кадр добавил бы
 * задержку ровно той строке, на которую оператор смотрит.
 */

/** Как часто проверяем, не истёк ли срок ответа на команду. */
const PENDING_TICK_MS = 1000;

/**
 * Чем сопровождается команда: откуда она пришла и когда по ней кликнули.
 *
 * clickedAtMono берётся у ВЫЗЫВАЮЩЕГО, а не здесь, и это важно: между нажатием
 * и вызовом place успевают отработать валидация, пересборка запроса и рендер, и
 * отметка, поставленная здесь, потеряла бы ровно ту задержку, которую оператор
 * чувствует. Компонент передаёт event.timeStamp — он на той же шкале, что
 * performance.now(), и ставится браузером в момент ГЕНЕРАЦИИ события, то есть
 * захватывает и занятость главного потока.
 */
export interface CommandMeta {
  source: CommandSource;
  clickedAtMono: number;
}

/**
 * Исход отправки.
 *
 * Размеченное объединение, а не расширенный мешок: trace_id есть только в
 * успехе, и тип обязан это отражать. Без trace_id панель быстрых действий не
 * может ни показать судьбу СВОЕГО действия, ни измерить задержку — до вехи M5
 * он молча выбрасывался.
 */
export type SendOutcome =
  | {
      ok: true;
      traceId: string;
      clientOrderId: string;
      /**
       * Отказы части аккаунтов когорты: остальные команды ушли. Пусто у
       * команды одного аккаунта — там отказ приходит ошибкой целиком.
       */
      partial: string[];
      /**
       * Все ушедшие команды — по одной на аккаунт цели. traceId выше — первая
       * из них; ждать вердикт только её значило бы сказать «принят биржей» за
       * всю когорту по ответу одного аккаунта.
       */
      commands: { traceId: string; account: string }[];
    }
  | { ok: false; error: string };

interface OrdersStore extends OrdersState {
  ingestOrder: (o: Order) => void;
  ingestResult: (traceId: string, res: CommandResult) => void;

  hydrate: () => Promise<void>;
  /**
   * Цель — обязательным параметром у каждой команды: стор не знает, какой
   * аккаунт выбран, и не должен — иначе забытый выбор стал бы умолчанием.
   */
  place: (req: PlaceOrderRequest, target: CommandTarget, meta?: CommandMeta) => Promise<SendOutcome>;
  /**
   * Пробный (dry-only) ордер. Тот же путь и то же ожидание вердикта по
   * trace_id, что у настоящего: симуляция обязана мерить наш реальный путь
   * команды, включая его задержку, — иначе она мерит что-то другое.
   */
  placePaper: (
    req: PlacePaperOrderRequest,
    target: CommandTarget,
    meta?: CommandMeta,
  ) => Promise<SendOutcome>;
  /**
   * Снятие ордера. Аккаунт — из строки ордера: владелец у ордера один, и
   * выбор в панели здесь ни при чём (снимать можно и ордер другого аккаунта).
   */
  cancel: (clientOrderID: string, account?: string, meta?: CommandMeta) => Promise<SendOutcome>;
  cancelAll: (
    target: CommandTarget,
    conditionID?: string,
    meta?: CommandMeta,
  ) => Promise<SendOutcome>;
}

/** Умолчание для вызовов без метаданных: клик считается «сейчас». */
function defaultMeta(): CommandMeta {
  return { source: "form", clickedAtMono: performance.now() };
}

export const useOrders = create<OrdersStore>((set, get) => ({
  ...emptyOrders(),

  ingestOrder: (o) => set((s) => reduceOrder(s, o)),

  /**
   * Применяет вердикт и снимает замер задержки.
   *
   * Запись «в полёте» читается ДО редьюсера: он её удаляет, а именно в ней
   * лежит момент клика. Замер уходит в отдельный store — положить его в
   * OrdersState значило бы перерисовывать таблицу ордеров на каждую команду.
   */
  ingestResult: (traceId, res) => {
    const pending = get().pending[traceId];
    set((s) => reduceResult(s, traceId, res, Date.now()));
    if (!pending) return;

    // Замер — да, судьба ордера — нет. latency_ms по контракту обязателен, но
    // содержимое шины не доверенное (конверт мог опубликовать руками во время
    // инцидента или чужой публикатор), а вычитание отсутствующего числа даёт
    // NaN, который дальше просачивается в сортировку и квантили и делает
    // нечитаемой ВСЮ панель на двести замеров вперёд. Вердикт при этом
    // полноценен и применён выше: молчать о судьбе ордера из-за неизмеримой
    // задержки было бы куда хуже, чем потерять один замер.
    if (!Number.isFinite(res.latency_ms)) {
      console.warn("command_result без пригодного latency_ms: замер пропущен", res);
      return;
    }

    // Монотонные часы браузера с момента клика. Разность двух ДЛИТЕЛЬНОСТЕЙ
    // (этой и трейдерской) законна; разность отметок времени с разных часов —
    // нет, и command.accepted_at поэтому в арифметике не участвует (lib/latency).
    const now = performance.now();
    useLatency.getState().record({
      source: pending.source,
      kind: pending.kind,
      toAcceptedMs:
        pending.acceptedAtMono === null ? null : pending.acceptedAtMono - pending.clickedAtMono,
      roundTripMs: now - pending.clickedAtMono,
      traderMs: res.latency_ms,
      accepted: res.accepted,
      // Те же монотонные часы: по ним головное число отличает «плохо сейчас» от
      // «было плохо час назад» (lib/latency.recent).
      at: now,
    });
  },

  /**
   * Гидрация из REST — не оптимизация, а ЕДИНСТВЕННЫЙ путь восстановления.
   *
   * web_api отдаёт новому клиенту историю ТОЛЬКО стрима evt.tape (serveWS);
   * evt.orders и evt.commands мостятся живьём и не переигрываются вовсе. Значит,
   * всё, что случилось с ордерами за время разрыва, до вкладки не доедет
   * никогда, и без этого запроса список врал бы до перезагрузки страницы.
   *
   * Поэтому же она вызывается не только при монтировании, но и на КАЖДОМ
   * переподключении сокета (см. useConn.generation в OrdersPanel).
   *
   * Спрашивается status=all, а не open, и это не расточительство.
   * Исполненные ордера приезжали ТОЛЬКО живьём по сокету, и после перезагрузки
   * страницы список схлопывался в «нет ордеров» — при том, что полчаса назад по
   * этому рынку прошли филлы. Разбирают исполнение как раз потом, и колонка
   * контрагента в строке MATCHED без этого не показывалась бы почти никогда.
   * Потолок закрытых строк держит prune (CLOSED_KEEP), так что список не растёт.
   */
  hydrate: async () => {
    if (get().hydrating) return;
    set({ hydrating: true });
    try {
      const res = await api.orders({ status: "all", limit: 200 });
      // hydrateOpen, а не hydrateAll: он сохраняет строки команд «в полёте»,
      // а с ними — тот самый щит от гонки гидрации с записью у трейдера.
      // Терминальные строки он тоже принимает, просто раньше их в ответе не было.
      set((s) => hydrateOpen(s, res.orders, Date.now()));
      if (res.truncated) {
        // Обрезанный список означает, что оператор считает свою позицию меньше,
        // чем она есть, — молчать об этом нельзя.
        console.warn("список ордеров обрезан по limit: показаны не все");
      }
    } catch (err) {
      const message = err instanceof HttpError ? err.message : String(err);
      set({ hydrating: false, hydrateError: message });
    }
  },

  place: async (req, target, meta) =>
    sendCommand(set, "place", () => api.placeOrder(req, target), meta ?? defaultMeta(), "", req),
  placePaper: async (req, target, meta) =>
    // Без req пятым аргументом — и это не забывчивость: оптимистичную строку
    // пробный ордер не заводит. Он не попадает в `orders` ни на бирже, ни в
    // базе, и строка-призрак в списке означала бы деньги, стоящие в стакане.
    sendCommand(set, "place_paper", () => api.placePaperOrder(req, target), meta ?? defaultMeta()),
  cancel: async (clientOrderID, account, meta) =>
    sendCommand(
      set,
      "cancel",
      () => api.cancelOrder(clientOrderID, account),
      meta ?? defaultMeta(),
      clientOrderID,
    ),
  cancelAll: async (target, conditionID, meta) =>
    sendCommand(
      set,
      "cancel_all",
      () => api.cancelAll(target, conditionID),
      meta ?? defaultMeta(),
    ),
}));

type SetState = (fn: (s: OrdersState) => OrdersState) => void;

/**
 * Общий путь всех трёх команд: отправить, завести «в полёте», показать отказ.
 *
 * Состояние «отправляется» ключуется trace_id из ответа 202, а не
 * client_order_id: у cancel_all ордера нет вовсе, и по нему такая команда не
 * сматчилась бы никогда.
 */
async function sendCommand(
  set: SetState,
  kind: PendingKind,
  call: () => Promise<CommandsAccepted | { command: CommandAccepted }>,
  meta: CommandMeta,
  clientOrderID = "",
  req?: PlaceOrderRequest,
): Promise<SendOutcome> {
  try {
    const res = await call();
    // Монотонные часы: ответ 202 получен. Date.now() здесь не годится — он
    // умеет прыгнуть от коррекции NTP между кликом и ответом.
    const acceptedAtMono = performance.now();
    // Постановка и «снять все» отвечают списком (по команде на аккаунт цели),
    // снятие одного — одной командой. Дальше путь общий: «в полёте» и
    // оптимистичная строка заводятся на КАЖДУЮ ушедшую команду, иначе
    // вердикт второго аккаунта когорты некому было бы принять.
    const commands = "commands" in res ? res.commands : [res.command];
    const partial = "commands" in res ? (res.errors ?? []).map(rejectedText) : [];
    const first = commands[0];
    if (!first) {
      // web_api отвечает 202 только когда ушла хоть одна команда; пустой
      // список — нарушение контракта, и молча считать его успехом нельзя.
      return { ok: false, error: partial[0] ?? "web_api не вернул ни одной команды" };
    }
    for (const command of commands) {
      const clientOrderId = command.client_order_id ?? clientOrderID;
      set((s) =>
        startPending(s, {
          traceId: command.trace_id,
          kind,
          clientOrderId,
          startedAt: Date.now(),
          clickedAtMono: meta.clickedAtMono,
          acceptedAtMono,
          source: meta.source,
        }),
      );
      if (kind === "place" && req && command.client_order_id) {
        // Оптимистичная строка: статус PENDING по контракту означает «команда
        // принята, ответа биржи ещё нет» — ровно то, что сейчас и происходит.
        // Ранг PENDING самый низкий, поэтому любое серверное обновление её
        // перебьёт (см. mergeOrder).
        // С учётом вердикта: быстрый отказ гейта обгоняет этот ответ (см.
        // orders.addOptimistic), и строка без него висела бы PENDING.
        const row = optimisticOrder(command.client_order_id, req, command.account);
        set((s) => addOptimistic(s, row, command.trace_id));
      }
    }
    return {
      ok: true,
      traceId: first.trace_id,
      clientOrderId: first.client_order_id ?? clientOrderID,
      partial,
      commands: commands.map((c) => ({ traceId: c.trace_id, account: c.account ?? "" })),
    };
  } catch (err) {
    const message = err instanceof HttpError ? err.message : String(err);
    return { ok: false, error: message };
  }
}

/**
 * Заготовка строки для оптимистичного показа.
 *
 * Заполняется ИЗ ОТПРАВЛЕННОЙ КОМАНДЫ, а не пустышками. Это не косметика:
 * панель ордеров фильтрует строки по condition_id, а отметки в лесенке — по
 * asset_id, и строка с пустыми полями оказалась бы невидимой ровно на той
 * странице, где оператор её и поставил. Она же несёт цену и сторону, из которых
 * рисуется ПОЛЫЙ маркер уровня — тот самый «вижу свой ордер в стакане» ещё до
 * подтверждения биржей.
 *
 * Дожидаться серверного order_update нельзя: при отказе риск-гейта его не будет
 * вовсе, и оператор не увидел бы ни строки, ни причины.
 */
/** Отказ одного аккаунта когорты — строкой для показа. */
function rejectedText(r: { account: string; error: string }): string {
  return `${r.account.slice(0, 6)}…: ${r.error}`;
}

function optimisticOrder(clientOrderID: string, req: PlaceOrderRequest, account?: string): Order {
  const now = new Date().toISOString();
  return {
    client_order_id: clientOrderID,
    condition_id: req.condition_id,
    asset_id: req.asset_id,
    side: req.side,
    price: req.price,
    size: req.size,
    size_matched: "0",
    status: "PENDING",
    order_type: req.order_type,
    issued_by: "operator",
    ...(account ? { account } : {}),
    ...(req.expires_at ? { expires_at: req.expires_at } : {}),
    created_at: now,
    updated_at: now,
  };
}

/**
 * Тикер, помечающий команды без ответа.
 *
 * Живёт вне React, как и коалесцирование `received` в useConn: подписка на него
 * нужна одна на вкладку, а не одна на компонент.
 */
let pendingTimer: ReturnType<typeof setInterval> | null = null;

export function startPendingWatch(): void {
  if (pendingTimer !== null) return;
  pendingTimer = setInterval(() => {
    useOrders.setState((s) => expirePending(s, Date.now()));
  }, PENDING_TICK_MS);
}

/** Открытые ордера рынка — селектор для панели и для отметок в стакане. */
export function selectOpen(state: OrdersState, conditionId?: string | null): Order[] {
  return Object.values(state.orders)
    .filter((o) => !isTerminal(o.status))
    .filter((o) => !conditionId || o.condition_id === conditionId);
}

/**
 * «Снять все» у ВСЕХ аккаунтов набора — по рынку или везде.
 *
 * С 2026-10-07 общей цели у панели нет (у быстрых действий — строки, у формы —
 * свой выбор), и аварийный тормоз «снять все» обязан снимать всё, а не то, что
 * оказалось выбрано. web_api «снять все» без цели не принимает (400), поэтому
 * команда уходит на каждый аккаунт отдельно.
 *
 * Кому: загруженным по составу (незагруженному трейдер всё равно откажет) и
 * всем, у кого в сторе есть открытый ордер, — состав мог не доехать, а ордер
 * в стакане стоит. Отказы части аккаунтов не гасят остальных и уходят в
 * partial, как у команды когортой.
 */
export async function cancelEverywhere(
  conditionID: string | null,
  meta?: CommandMeta,
): Promise<SendOutcome> {
  const roster = useAccounts.getState().data;
  const accounts = new Set<string>();
  for (const a of roster?.accounts ?? []) if (a.loaded !== false) accounts.add(a.funder);
  for (const o of Object.values(useOrders.getState().orders)) {
    if (!o.account || isTerminal(o.status)) continue;
    if (conditionID && o.condition_id !== conditionID) continue;
    accounts.add(o.account);
  }
  if (accounts.size === 0) {
    return { ok: false, error: "снимать не у кого: состав аккаунтов неизвестен, открытых ордеров нет" };
  }

  const list = [...accounts];
  const { cancelAll } = useOrders.getState();
  const results = await Promise.all(
    list.map((account) => cancelAll({ account }, conditionID ?? undefined, meta)),
  );
  const commands: { traceId: string; account: string }[] = [];
  const partial: string[] = [];
  results.forEach((res, i) => {
    const who = accountLabel(roster, list[i]);
    if (res.ok) {
      commands.push(...res.commands);
      partial.push(...res.partial);
    } else {
      partial.push(`${who}: ${res.error}`);
    }
  });
  const first = commands[0];
  if (!first) return { ok: false, error: partial.join("; ") };
  return { ok: true, traceId: first.traceId, clientOrderId: "", partial, commands };
}
